"use client";

import React, { useMemo, useState } from "react";
import { MapPin, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
interface CommunityItem {
  id: string;
  name: string;
  description: string;
}
const COMMUNITIES: CommunityItem[] = [{
  id: "annapolis",
  name: "Annapolis",
  description: "Historic state capital on the Severn River"
}, {
  id: "glen-burnie",
  name: "Glen Burnie",
  description: "North county hub with quick dispatch along the Route 2 corridor"
}, {
  id: "severna-park",
  name: "Severna Park",
  description: "Peninsula neighborhoods situated between the Magothy and Severn Rivers"
}, {
  id: "pasadena",
  name: "Pasadena",
  description: "Chesapeake waterfront communities and residential properties"
}, {
  id: "gibson-island",
  name: "Gibson Island",
  description: "Private island community and coastal residential properties"
}, {
  id: "crofton",
  name: "Crofton",
  description: "Planned suburban community centered around Route 3 and Route 424"
}, {
  id: "odenton",
  name: "Odenton",
  description: "Fast-growing community adjacent to Fort Meade and rail transit lines"
}, {
  id: "edgewater",
  name: "Edgewater",
  description: "South River peninsula community with direct tidal water access"
}, {
  id: "arnold",
  name: "Arnold",
  description: "Severn River residential area along the College Parkway corridor"
}, {
  id: "millersville",
  name: "Millersville",
  description: "Central county residential hub with fast access via Interstate 97"
}];
export default function CommunitiesGrid() {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredCommunities = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return COMMUNITIES;
    return COMMUNITIES.filter(item => item.name.toLowerCase().includes(query));
  }, [searchQuery]);
  return <section id="annearundelcommunitiesgrid" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
            Cities & Communities We Serve in Anne Arundel County
          </h2>
        </div>

        {/* Live Filter Search Bar */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
            <Input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search your city..." aria-label="Search cities and communities in Anne Arundel County" className="pl-10 h-11 bg-background border-border rounded-lg text-foreground focus-visible:ring-primary shadow-xs" />
          </div>
        </div>

        {/* Communities Grid */}
        {filteredCommunities.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {filteredCommunities.map((item, i) => <Card key={item.id} data-index={i} className="bg-card text-card-foreground border border-border shadow-xs hover:shadow-sm transition-shadow rounded-lg p-0 flex flex-col justify-between">
                <CardHeader className="p-5 pb-2">
                  <div className="flex items-center gap-2 mb-1.5 text-primary">
                    <MapPin className="size-4 shrink-0" aria-hidden="true" />
                    <CardTitle className="text-lg font-semibold tracking-tight text-card-foreground">
                      {item.name}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-5 pt-0">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>)}
          </div> : <div className="text-center py-12 border border-dashed border-border rounded-lg max-w-md mx-auto">
            <p className="text-sm text-muted-foreground">
              No matching community found for &ldquo;{searchQuery}&rdquo;.
            </p>
          </div>}

        {/* Emergency Restoration CTA Callout */}
        <div className="mt-16 pt-12 border-t border-border flex flex-col sm:flex-row items-center justify-center text-center sm:text-left gap-4 sm:gap-6">
          <p className="text-base sm:text-lg font-medium text-foreground">
            Need emergency restoration services?
          </p>
          <Button asChild size="lg" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-transform hover:-translate-y-0.5">
            <Link to="/ContactRequestServicePage">
              Request Emergency Service
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}